import React, { memo, ReactNode, useCallback, useState } from "react";
import {
  Text,
  VStack,
  Tooltip,
  Divider,
  HStack,
  Box,
  Spacer,
  Stack,
  Spinner,
} from "@chakra-ui/react";
import colors from "app/colors";
import {
  getDistributionGroupMention,
  getFullName,
} from "modules/utils/Functions";
import {
  ExternalIcon,
  InboxIcon,
  InboxSlashIcon,
  UserEmailCircleIcon,
} from "modules/icons";

import InternalAdminUserAvatar from "./internal-admin-user-avatar";
import useBurstQuery from "modules/hooks/use-burst-query";
import { CALLOUT_KEY } from "modules/utils/Constants";
import {
  InternalAdminDistributionGroupFragment,
  InternalAdminDistributionGroupQuery,
  InternalAdminDistributionGroupQueryVariables,
} from "gql/graphql";
import INTERNAL_ADMIN_DISTRIBUTION_GROUP_QUERY from "../_networking/internal-admin-distribution-group";

interface IProps {
  distributionGroupId: string;
  openDelay?: number;
  children: ReactNode;
}

const InternalAdminDistributionGroupTooltip = ({
  distributionGroupId,
  openDelay,
  children,
}: IProps) => {
  const [distributionGroup, setDistributionGroup] =
    useState<InternalAdminDistributionGroupFragment | null>();
  const [internalAdminDistributionGroupAPI] = useBurstQuery<
    InternalAdminDistributionGroupQuery,
    InternalAdminDistributionGroupQueryVariables
  >(INTERNAL_ADMIN_DISTRIBUTION_GROUP_QUERY);

  const getDistributionGroup = useCallback(async () => {
    if (distributionGroupId) {
      const response = await internalAdminDistributionGroupAPI({
        variables: { id: distributionGroupId },
      });
      if (response.data?.internalAdminDistributionGroup.length === 1) {
        setDistributionGroup(response.data.internalAdminDistributionGroup[0]);
      }
      return;
    }
    setDistributionGroup(undefined);
  }, [distributionGroupId, internalAdminDistributionGroupAPI]);

  const isNotification = useCallback(
    (userId: string) =>
      distributionGroup &&
      distributionGroup.notificationUserIdList.includes(userId),
    [distributionGroup],
  );

  return (
    <Tooltip
      backgroundColor={colors.white}
      color={colors.grayMain}
      hasArrow
      borderRadius={16}
      padding={3}
      openDelay={openDelay}
      onOpen={getDistributionGroup}
      placement="bottom-start"
      w={394}
      label={
        <VStack alignItems="flex-start">
          {!distributionGroup ? (
            <Spinner />
          ) : (
            <>
              <HStack>
                <Text color={colors.blue} fontWeight={700} fontSize={18}>
                  {getDistributionGroupMention(distributionGroup)}
                </Text>
                {distributionGroup.emailUserIdList.length > 0 && (
                  <ExternalIcon fill={colors.transparent} />
                )}
              </HStack>
              <Text>{`Members (${
                distributionGroup.userIdList.length +
                distributionGroup.emailUserIdList.length
              })`}</Text>
              <Divider />
              <Stack spacing={1} divider={<Divider />} w="full">
                {[...distributionGroup.userList]
                  .sort((a, b) =>
                    (a.lastName || "").localeCompare(b.lastName || ""),
                  )
                  .map((user) => (
                    <Box w="100%" key={user.id}>
                      <HStack>
                        <InternalAdminUserAvatar
                          avatarUrl={user.avatarUrl}
                          active={user.active}
                          firstName={user.firstName}
                          lastName={user.lastName}
                          roleType={user.roleType}
                          size="xs"
                        />
                        <Text
                          color={colors.blue}
                        >{`${CALLOUT_KEY}${user.callout}`}</Text>
                        <Spacer />
                        <Box>
                          {isNotification(user.id) ? (
                            <InboxIcon boxSize={4} fill={colors.blue} />
                          ) : (
                            <InboxSlashIcon
                              boxSize={4}
                              fill={colors.grayMain}
                            />
                          )}
                        </Box>
                      </HStack>
                    </Box>
                  ))}
              </Stack>
              {!!distributionGroup.emailUserList.length && (
                <Stack pt={4} w="full">
                  <Text fontSize="xs" color={colors.grayMain}>
                    EXTERNAL CONTACTS
                  </Text>
                  <Stack spacing={1} divider={<Divider />}>
                    {[...distributionGroup.emailUserList]
                      .sort((a, b) =>
                        (a.email || "").localeCompare(b.email || ""),
                      )
                      .map((emailUser) => (
                        <Box w="100%" key={emailUser.id}>
                          <HStack>
                            <UserEmailCircleIcon
                              boxSize={6}
                              fill={colors.white}
                            />
                            <Stack spacing={0}>
                              {emailUser.firstName && (
                                <Text fontSize={14} color={colors.gray50}>
                                  {getFullName(emailUser.firstName, "")}
                                </Text>
                              )}
                              <Text fontSize={14} color={colors.blueGreen}>
                                {emailUser.email}
                              </Text>
                            </Stack>
                            <Spacer />
                          </HStack>
                        </Box>
                      ))}
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </VStack>
      }
    >
      {children}
    </Tooltip>
  );
};

export default memo(InternalAdminDistributionGroupTooltip);
